// ContactDetailDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';
import { useConfirm } from '../../../components/common/ConfirmDialog';

export const ContactDetailDialog = ({ open, onOpenChange, contactEntry, onSuccess, canEditAccount }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();
  const [formData, setFormData] = useState({
    contact_type: 'email',
    address_type: 'office',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    email_type: 'office',
    email_address: '',
    phone_type: 'office',
    phone_number: '',
    nickname: '',
    is_primary: false,
    notes: '',
  });

  const addressTypes = [
    { value: 'office', label: 'Office' },
    { value: 'billing', label: 'Billing' },
    { value: 'mailing', label: 'Mailing' },
    { value: 'other', label: 'Other' },
  ];

  const emailTypes = [
    { value: 'office', label: 'Office' },
    { value: 'billing', label: 'Billing' },
    { value: 'other', label: 'Other' },
  ];

  const phoneTypes = [
    { value: 'office', label: 'Office' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'fax', label: 'Fax' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    if (contactEntry) {
      setFormData({
        contact_type: contactEntry.contact_type || 'email',
        address_type: contactEntry.address_type || 'office',
        street: contactEntry.street || '',
        city: contactEntry.city || '',
        state: contactEntry.state || '',
        zip_code: contactEntry.zip_code || '',
        country: contactEntry.country || 'USA',
        email_type: contactEntry.email_type || 'office',
        email_address: contactEntry.email_address || '',
        phone_type: contactEntry.phone_type || 'office',
        phone_number: contactEntry.phone_number || '',
        nickname: contactEntry.nickname || '',
        is_primary: contactEntry.is_primary || false,
        notes: contactEntry.notes || '',
      });
    }
  }, [contactEntry]);

  const handleSave = async () => {
    if (!contactEntry?.id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/contact-entries/${contactEntry.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Contact updated');
        setIsEditing(false);
        onSuccess?.();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Failed to update contact:', error);
      toast.error('Failed to update contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contactEntry?.id) return;
    if (!await confirm({ title: 'Are you sure you want to delete this contact entry?', destructive: true })) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${window.location.origin}/api/contact-entries/${contactEntry.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Contact deleted');
        onOpenChange(false);
        onSuccess?.();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Failed to delete contact:', error);
      toast.error('Failed to delete contact');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (contactEntry) {
      setFormData({
        contact_type: contactEntry.contact_type || 'email',
        address_type: contactEntry.address_type || 'office',
        street: contactEntry.street || '',
        city: contactEntry.city || '',
        state: contactEntry.state || '',
        zip_code: contactEntry.zip_code || '',
        country: contactEntry.country || 'USA',
        email_type: contactEntry.email_type || 'office',
        email_address: contactEntry.email_address || '',
        phone_type: contactEntry.phone_type || 'office',
        phone_number: contactEntry.phone_number || '',
        nickname: contactEntry.nickname || '',
        is_primary: contactEntry.is_primary || false,
        notes: contactEntry.notes || '',
      });
    }
    setIsEditing(false);
  };

  if (!contactEntry) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contact-detail-dialog">
        <DialogHeader>
          <DialogTitle>Contact Detail</DialogTitle>
        </DialogHeader>

        {isEditing ? (
          <div className="space-y-4 py-2">
            {formData.contact_type === 'address' && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.address_type}
                    onValueChange={(v) => setFormData({ ...formData, address_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {addressTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Street Address</Label>
                  <Input
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label>ZIP Code</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    />
                  </div>
                </div>
              </>
            )}

            {formData.contact_type === 'email' && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.email_type}
                    onValueChange={(v) => setFormData({ ...formData, email_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emailTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                  />
                </div>
              </>
            )}

            {formData.contact_type === 'phone' && (
              <>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={formData.phone_type}
                    onValueChange={(v) => setFormData({ ...formData, phone_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Nickname (optional)</Label>
              <Input
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                placeholder="e.g., Farm Office, Personal Cell"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-contact-primary"
                checked={formData.is_primary}
                onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="edit-contact-primary" className="text-sm cursor-pointer">
                Primary contact
              </Label>
            </div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="p-4 bg-muted/30 rounded">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    {contactEntry.contact_type === 'address' && (
                      <>
                        <Badge variant="outline" className="text-xs">{contactEntry.address_type}</Badge>
                        Address
                      </>
                    )}
                    {contactEntry.contact_type === 'email' && (
                      <>
                        <Badge variant="outline" className="text-xs">{contactEntry.email_type}</Badge>
                        Email
                      </>
                    )}
                    {contactEntry.contact_type === 'phone' && (
                      <>
                        <Badge variant="outline" className="text-xs">{contactEntry.phone_type}</Badge>
                        Phone
                      </>
                    )}
                    {contactEntry.is_primary && (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    )}
                  </h3>
                  {contactEntry.nickname && (
                    <p className="text-sm text-muted-foreground mt-1">({contactEntry.nickname})</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                {contactEntry.contact_type === 'address' && (
                  <>
                    <div>
                      <p className="font-medium">{contactEntry.street}</p>
                      <p className="text-muted-foreground">
                        {contactEntry.city}, {contactEntry.state} {contactEntry.zip_code}
                      </p>
                      {contactEntry.country !== 'USA' && (
                        <p className="text-muted-foreground">{contactEntry.country}</p>
                      )}
                    </div>
                  </>
                )}

                {contactEntry.contact_type === 'email' && (
                  <div>
                    <p className="font-medium">{contactEntry.email_address}</p>
                  </div>
                )}

                {contactEntry.contact_type === 'phone' && (
                  <div>
                    <p className="font-medium font-mono">{contactEntry.phone_number}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {isEditing ? (
            <div className="flex items-center justify-between w-full">
              <Button 
                type="button" 
                variant="ghost" 
                size="icon"
                onClick={handleDelete}
                disabled={saving}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-between w-full">
              {canEditAccount && (
                <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)} data-testid="edit-contact-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

